import React from 'react'
import { Copy, Check } from 'lucide-react'
import loggingService from '../services/loggingService'
import { createLogger } from '../utils/logger'

const logger = createLogger('ErrorBoundary')

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      copied: false
    }
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true }
  }

  componentDidCatch(error, errorInfo) {
    // Log the error
    loggingService.error('React Component Error', {
      type: 'react_error',
      error: error.toString(),
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      props: this.props,
      timestamp: new Date().toISOString()
    })

    this.setState({
      error,
      errorInfo
    })
  }

  copyErrorToClipboard = () => {
    const errorText = `Error: ${this.state.error?.toString()}\n\nStack:\n${this.state.error?.stack}\n\nComponent Stack:\n${this.state.errorInfo?.componentStack}`

    logger.debug('Attempting to copy error text:', errorText)
    logger.debug('Clipboard API available:', !!navigator.clipboard)
    logger.debug('Secure context:', window.isSecureContext)

    // Try modern clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(errorText)
        .then(() => {
          logger.debug('Successfully copied to clipboard')
          this.setState({ copied: true })
          setTimeout(() => {
            this.setState({ copied: false })
          }, 2000)
        })
        .catch((err) => {
          logger.error('Failed to copy error to clipboard:', err)
          this.fallbackCopyToClipboard(errorText)
        })
    } else {
      logger.debug('Using fallback copy method')
      // Fallback for older browsers or non-secure contexts
      this.fallbackCopyToClipboard(errorText)
    }
  }

  fallbackCopyToClipboard = (text) => {
    logger.debug('Using fallback copy method with text:', text)

    // Create a temporary textarea element
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed'
    textArea.style.left = '-999999px'
    textArea.style.top = '-999999px'
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()

    try {
      const successful = document.execCommand('copy')
      logger.debug('execCommand copy result:', successful)
      if (successful) {
        this.setState({ copied: true })
        setTimeout(() => {
          this.setState({ copied: false })
        }, 2000)
      } else {
        logger.error('Fallback copy failed')
        alert(
          'Failed to copy error details. Please select and copy manually from the text area below.'
        )
      }
    } catch (err) {
      logger.error('Fallback copy failed:', err)
      alert(
        'Failed to copy error details. Please select and copy manually from the text area below.'
      )
    }

    document.body.removeChild(textArea)
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg
                  className="h-8 w-8 text-red-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">Something went wrong</h3>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600">
                We're sorry, but something unexpected happened. Our team has been notified and is
                working to fix this issue.
              </p>
            </div>

            {process.env.NODE_ENV === 'development' && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-700">
                    Error Details (Development Only)
                  </h4>
                  <button
                    onClick={this.copyErrorToClipboard}
                    className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
                    title="Copy error details"
                  >
                    {this.state.copied ? (
                      <>
                        <Check className="w-3 h-3 text-green-600" />
                        <span className="text-green-600">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>
                <textarea
                  readOnly
                  className="w-full h-32 p-3 bg-gray-100 rounded text-xs font-mono text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 cursor-text"
                  value={`Error: ${this.state.error?.toString()}\n\nStack:\n${this.state.error?.stack}\n\nComponent Stack:\n${this.state.errorInfo?.componentStack}`}
                  onClick={(e) => e.target.select()}
                  title="Click to select all text for manual copying"
                />
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={() => window.location.reload()}
                className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Reload Page
              </button>
              <button
                onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
